import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import { log } from '../../lib/logger';
import type { Project, ProjectStatus } from '../../types';
import { ACTIVE_STATUSES } from './config';

// ── Activity log helpers ──
export async function logActivity(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'project', entity_id: eId, logged_by: uid }); } catch { /* */ }
}
export async function logAudit(projectId: string, action: string, details: Record<string, unknown> | null, oldVal: string | null, newVal: string | null, uid: string) {
  try { await supabase.from('project_audit').insert({ project_id: projectId, action, details, old_value: oldVal, new_value: newVal, performed_by: uid }); } catch { /* */ }
}

// ── Types ──
export interface ProjectRow extends Project { client_display?: string; manager_name?: string; phase_count?: number; }
export interface ProjectFilters { status: string | null; search: string; }
export interface ProjectSummary { activeCount: number; totalBudget: number; totalSpent: number; blockingGates: number; }
export interface TeamUser { id: string; first_name: string; last_name: string; }

export function useProjects(businessId: string | undefined) {
  const [all, setAll] = useState<ProjectRow[]>([]);
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProjectFilters>({ status: null, search: '' });
  const [blockingGates, setBlockingGates] = useState(0);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      const [projRes, usersRes, clientsRes, gatesRes] = await Promise.all([
        supabase.from('projects').select('*').eq('business_id', businessId)
          .order('created_at', { ascending: false }).limit(200).abortSignal(ctrl.signal),
        supabase.from('users').select('id, first_name, last_name').eq('business_id', businessId)
          .eq('status', 'active').abortSignal(ctrl.signal),
        supabase.from('clients').select('id, name').eq('business_id', businessId)
          .abortSignal(ctrl.signal),
        supabase.from('project_gates').select('id, status, project_id')
          .in('status', ['pending', 'blocking']).abortSignal(ctrl.signal),
      ]);
      if (projRes.error) throw projRes.error;
      const uMap = new Map((usersRes.data ?? []).map(u => [u.id, `${u.first_name} ${u.last_name}`]));
      const cMap = new Map((clientsRes.data ?? []).map(c => [c.id, c.name]));
      // Count blocking gates per project belonging to this business
      const projIds = new Set((projRes.data ?? []).map(p => p.id));
      const bgCount = (gatesRes.data ?? []).filter(g => projIds.has(g.project_id) && (g.status === 'blocking' || g.status === 'pending')).length;
      const rows = (projRes.data ?? []).map(p => ({
        ...p,
        client_display: p.client_id ? cMap.get(p.client_id) ?? p.client_name : p.client_name,
        manager_name: p.created_by ? uMap.get(p.created_by) : undefined,
      })) as ProjectRow[];
      if (mountedRef.current) { setAll(rows); setUsers((usersRes.data ?? []) as TeamUser[]); setClients((clientsRes.data ?? []) as { id: string; name: string }[]); setBlockingGates(bgCount); }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['projects'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const filtered = useMemo(() => {
    let list = all;
    if (filters.status) list = list.filter(p => p.status === filters.status);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.client_display ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [all, filters]);

  const summary = useMemo<ProjectSummary>(() => {
    const active = all.filter(p => ACTIVE_STATUSES.includes(p.status));
    return {
      activeCount: active.length,
      totalBudget: active.reduce((s, p) => s + (p.budget ?? 0), 0),
      totalSpent: active.reduce((s, p) => s + (p.estimated_cost ?? 0), 0),
      blockingGates,
    };
  }, [all, blockingGates]);

  // ── Create ──
  const createProject = useCallback(async (data: Partial<Project>, phases: { name: string; tasks: { name: string; is_required: boolean; estimated_hours: number | null }[] }[] | null, userId: string): Promise<string | null> => {
    if (!businessId) return null;
    // §Work.CreateProjectFailsSwallowedError P2 (admin pair): one atomic SECDEF
    // RPC — create_full_project (migration 20261009021936) — replaces the
    // project → phases → tasks insert chain. The RPC writes project_audit +
    // activity_log itself (actor = the session's public user), so the manual
    // logActivity/logAudit calls are gone from this path. userId stays in the
    // signature for the callers; the server resolves the actor.
    void userId;
    try {
      const phasePayload = (phases ?? []).map((ph, i) => ({ id: crypto.randomUUID(), name: ph.name, sort_order: i, status: 'pending', progress: 0, tasks: ph.tasks }));
      const taskPayload = phasePayload.flatMap((ph) => ph.tasks.map((tk, ti) => ({
        id: crypto.randomUUID(), phase_id: ph.id, name: tk.name, sort_order: ti, status: 'not_started', is_required: tk.is_required, estimated_hours: tk.estimated_hours,
      })));
      const { data: env, error: e } = await supabase.rpc('create_full_project', {
        p_input: {
          name: data.name, description: data.description ?? null, source_type: data.source_type ?? 'new_client',
          template_slug: data.template_slug ?? null, client_id: data.client_id ?? null, client_name: data.client_name ?? null,
          address: data.address ?? null, latitude: data.latitude ?? null, longitude: data.longitude ?? null,
          proposed_start: data.proposed_start ?? null, proposed_end: data.proposed_end ?? null,
          is_flexible: data.is_flexible ?? true, status: 'lead' as ProjectStatus,
          phases: phasePayload.map(({ tasks: _t, ...ph }) => ph), tasks: taskPayload, gates: [],
        },
      });
      const result = (env ?? {}) as { success?: boolean; project_id?: string; error?: string; message?: string; sqlstate?: string };
      if (e || !result.success || !result.project_id) {
        log.error('[useProjects.createProject] create_full_project rejected', e ?? undefined, { error: result.error, sqlstate: result.sqlstate, message: result.message });
        return null;
      }
      void fetch(); return result.project_id;
    } catch (err) { log.error('[useProjects.createProject] threw', err); return null; }
  }, [businessId, fetch]);

  // ── Update status ��─
  const updateStatus = useCallback(async (id: string, status: string, userId: string, reason?: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const old = all.find(p => p.id === id);
      const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
      if (status === 'complete') updates.actual_end_date = new Date().toISOString().split('T')[0];
      if (status === 'in_progress' && !old?.actual_start) updates.actual_start = new Date().toISOString().split('T')[0];
      const { error: e } = await supabase.from('projects').update(updates).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await logActivity(businessId, `project_${status}`, `Project status → ${status}${reason ? `: ${reason}` : ''}`, id, userId);
      await logAudit(id, 'status_change', reason ? { reason } : null, old?.status ?? null, status, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, all, fetch]);

  // ── Update project fields ──
  const updateProject = useCallback(async (id: string, data: Partial<Project>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('projects').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await logActivity(businessId, 'project_updated', `Project updated: ${data.name ?? id}`, id, userId);
      await logAudit(id, 'updated', data as Record<string, unknown>, null, null, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { projects: filtered, allProjects: all, users, clients, loading, error, summary, filters, setFilters, refresh: fetch, createProject, updateStatus, updateProject };
}
